"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";
import { onInpSafeButtonClick } from "@/helpers/inpSafeForm";

import React from "react";
import { Controller } from "react-hook-form";
import styles from "./ProgramSelectorBanner.module.scss";
import ModalPortal from "@/components/organisms/HeaderShared/ModalPortal";
import CountryDropdownGlobalWhite from "../FormComponents/CustomDropDownWhite/CustomDropdownGlobalWhite";
import { ALL_COUNTRIES } from "@/context/countries";
import cn from "clsx";
import Checkbox from "../FormComponents/Checkbox";
import { CertificationCard, ProgramBrandCard } from "./ProgramCards";
import UnivoImage from "@/components/common/UnivoImage";
import { IMAGE_ASSETS } from "@/constants/images";
import { useScrollToHashSection } from "@/helpers/useScrollToHashSection";
import {
  RESULT_HIGHLIGHTS,
  STREAMS,
  type ProgramSelectorBannerProps,
} from "./model/programSelectorBanner.types";
import { useProgramSelectorBannerController } from "./controller/useProgramSelectorBannerController";

export const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 6 9 17l-5-5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M19 12H5m6-7-7 7 7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProgramSelectorBanner: React.FC<ProgramSelectorBannerProps> = (props) => {
  const sectionRef = React.useRef<HTMLElement>(null);
  useScrollToHashSection(sectionRef, "#program-finder");

  const {
    isOpen,
    step,
    answers,
    programs,
    certificate,
    control,
    errors,
    otp,
    showOtp,
    otpResponse,
    otpError,
    isSendingOtp,
    resendCooldown,
    isSubmittingForm,
    isProceeding,
    proceedError,
    selectedProgramIndex,
    selectedCertIndex,
    totalQuestions,
    currentQuestion,
    currentOptions,
    questionsProgressPct,
    isInternational,
    isFormValid,
    canSubmitForm,
    canProceed,
    questionIndex,
    otpDisplayCountryCode,
    otpDisplayMobile,
    inputsRef,
    openModal,
    closeModal,
    handleSelectAnswer,
    handleGoBack,
    handleFieldChange,
    handleFieldBlur,
    updateCountryCode,
    handleValidate,
    handleEditNumber,
    handleResendOtp,
    formatCooldown,
    handleFormSubmit,
    selectProgram,
    selectCertificate,
    handleProceed,
    handleSetOtp,
    handleOtpKeyDown,
    handleOtpPaste,
  } = useProgramSelectorBannerController(props);

  const renderLeftPane = () => (
    <div className={styles.leftPane}>
      <h3 id="program-selector-title" className={styles.modalTitle}>
        Not Sure What To Choose?{" "}
        <span className={styles.gradient}>We&rsquo;ve </span>
        <br className="hidden lg:block" />
        <span className={styles.gradient}>Got You.</span>
      </h3>
      <p className={styles.modalSubtitle}>
        Answer a few quick questions and get your perfect match.
      </p>
      <UnivoImage
        className={styles.modalImage}
        src={IMAGE_ASSETS.programFinderModal}
        alt="Find your perfect program"
        width={480}
        height={320}
        sizes="(max-width: 768px) 90vw, 480px"
        loading="lazy"
        fetchPriority="low"
      />
      <div className="sm:hidden w-full">
        <button
          type="button"
          className={`${styles.goBack}  lg:flex ${
            questionsProgressPct > 50 ? styles.visible : styles.hidden
          }`}
          onClick={handleGoBack}
        >
          <ArrowLeftIcon />
          <span>Go Back</span>
        </button>
      </div>
    </div>
  );

  const renderQuestions = () => (
    <div className={styles.rightPane}>
      <div className={styles.progressBlock}>
        <span className={styles.stepLabel}>
          Step {questionIndex + 1} of {totalQuestions}
        </span>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${questionsProgressPct}%` }}
          />
        </div>
      </div>

      <div className={styles.questionBlock}>
        <h4 className={styles.questionTitle}>{currentQuestion.title}</h4>
        <div
          className={`${styles.options} ${
            currentOptions.length > 4 ? styles?.optionsWrap : ""
          }`}
        >
          {currentOptions.map((opt) => {
            const selected = answers[currentQuestion.id] === opt.id;
            const isChip = currentOptions.length > 4;
            return (
              <button
                key={opt.id}
                type="button"
                className={`${isChip ? styles.optionChip : styles.option} ${questionIndex !== 1 ? "w-full" : ""} ${
                  selected
                    ? isChip
                      ? styles.optionChipActive
                      : styles.optionActive
                    : ""
                }`}
                onClick={() => handleSelectAnswer(opt.id)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="sm:flex hidden">
        <button
          type="button"
          className={`${styles.goBack}  ${questionsProgressPct > 50 ? styles.visible : styles.hidden}`}
          onClick={handleGoBack}
        >
          <ArrowLeftIcon />
          <span>Go Back</span>
        </button>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className={styles.rightPane}>
      <div
        className={`${styles.formDivider}  ${questionsProgressPct > 90 ? "mt-5" : ""}`}
      />
      <InpSafeForm className={styles.formBlock} onSubmit={handleFormSubmit} noValidate>
        <h4 className={styles.formHeading}>Please Fill in Your Details</h4>

        <div className={styles.formField}>
          <Controller
            name="fullName"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={field.value ?? ""}
                  onChange={(e) => handleFieldChange("fullName", e.target.value)}
                  onBlur={() => {
                    field.onBlur();
                    handleFieldBlur("fullName");
                  }}
                  disabled={showOtp}
                  style={showOtp ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  required
                />
                {(fieldState.error?.message ?? errors.fullName) && (
                  <p
                    style={{
                      color: "#d92d20",
                      fontSize: "0.75rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {fieldState.error?.message ?? errors.fullName}
                  </p>
                )}
              </>
            )}
          />
        </div>
        <div className={styles.formField}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={field.value ?? ""}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  onBlur={() => {
                    field.onBlur();
                    handleFieldBlur("email");
                  }}
                  disabled={showOtp}
                  style={showOtp ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  required
                />
                {(fieldState.error?.message ?? errors.email) && (
                  <p
                    style={{
                      color: "#d92d20",
                      fontSize: "0.75rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {fieldState.error?.message ?? errors.email}
                  </p>
                )}
              </>
            )}
          />
        </div>

        <div className={styles.formRow}>
          <div
            className={`${styles.formField} ${styles.codeField}`}
            style={
              showOtp
                ? {
                    cursor: "not-allowed",
                    pointerEvents: "none",
                    opacity: 0.6,
                  }
                : undefined
            }
          >
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <CountryDropdownGlobalWhite
                  onChangeFunc={(value) => handleFieldChange("countryCode", value)}
                  countries={ALL_COUNTRIES}
                  countryCode={field.value}
                  updateCountryCode={updateCountryCode}
                  additionalStyle={cn(styles.text_field, "lining-nums")}
                  isDark={false}
                  isBrand={true}
                />
              )}
            />
          </div>
          <div className={`${styles.formField} ${styles.mobileField}`}>
            <Controller
              name="mobile"
              control={control}
              render={({ field }) => (
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Mobile No."
                  className="lining-nums"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    handleFieldChange("mobile", e.target.value.replace(/\D/g, ""))
                  }
                  onBlur={() => {
                    field.onBlur();
                    handleFieldBlur("mobile");
                  }}
                  disabled={showOtp}
                  style={
                    showOtp ? { opacity: 0.5, cursor: "not-allowed" } : undefined
                  }
                  required
                />
              )}
            />
          </div>
          {!isInternational && (
            <div className={`${styles.formField} ${styles.validateField}`}>
              <button
                type="button"
                onClick={handleValidate}
                disabled={isSendingOtp || showOtp}
                className={styles.validateBtn}
              >
                {isSendingOtp
                  ? "Sending..."
                  : showOtp
                    ? "OTP Sent"
                    : "Validate"}
              </button>
            </div>
          )}
        </div>
        {errors.mobile && (
          <p
            style={{
              color: "#d92d20",
              fontSize: "0.75rem",
              marginTop: "-.75rem",
            }}
          >
            {errors.mobile}
          </p>
        )}

        {!isInternational && showOtp && (
          <div className={styles.otpBlock}>
            <div className={styles.otpHeader}>
              <span className={styles.otpLabel}>Enter the 6-digit OTP</span>
              <div className={styles.otpSubLabel}>
                <span>
                  Sent to {otpDisplayCountryCode}{" "}
                  {otpResponse?.mobile ?? otpDisplayMobile}
                </span>
                {resendCooldown < 1 && (
                  <button
                    type="button"
                    className={styles.editNumberBtn}
                    onClick={handleEditNumber}
                  >
                    Edit number
                  </button>
                )}
              </div>
            </div>
            <div className={styles.otpRow}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  ref={(el) => {
                    inputsRef.current[index] = el;
                  }}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleSetOtp(e.target.value, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  onPaste={handleOtpPaste}
                  className={`${styles.otpInput} lining-nums`}
                  required
                />
              ))}
            </div>
            <div className={styles.otpResendRow}>
              {resendCooldown > 0 ? (
                <span className={styles.otpResendMuted}>
                  Resend OTP in <strong>{formatCooldown(resendCooldown)}</strong>
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.otpResendBtn}
                  onClick={handleResendOtp}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? "Sending..." : "Resend OTP"}
                </button>
              )}
            </div>
          </div>
        )}
        {otpError && (
          <p
            style={{
              color: "#d92d20",
              fontSize: "0.75rem",
              marginTop: "0.25rem",
              textAlign: "center",
            }}
          >
            {otpError}
          </p>
        )}

        <label className={styles.consentRow}>
          <div className="gap-4 flex">
            <Controller
              name="consent"
              control={control}
              render={({ field }) => (
                <Checkbox
                  name="consent"
                  value={field.value}
                  onChangeFunc={(value: boolean) => {
                    handleFieldChange("consent", value);
                  }}
                  style={{ padding: "0.4rem" }}
                  disabled={isSubmittingForm}
                />
              )}
            />
            <span className={styles.consentText}>
              By entering these details I agree that Amity University Online and
              its associates can contact me with updates & notifications via
              Email, SMS, WhatsApp, and Voice call as per its{" "}
              <a
                href="https://amityonline.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  textDecorationColor: "inherit",
                  cursor: "pointer",
                }}
              >
                Privacy Policy
              </a>
              . This consent will override any registration for DNC / NDNC.
            </span>
          </div>
          {errors.consent && (
            <p
              className="absolute -bottom-4"
              style={{ color: "#d92d20", fontSize: "0.7rem" }}
            >
              {errors.consent}
            </p>
          )}
        </label>

        <button
          type="button"
          onClick={onInpSafeButtonClick}
          className={styles.primaryCta}
          disabled={isSubmittingForm || isSendingOtp}
          style={
            isSubmittingForm || isSendingOtp ? { opacity: 0.5, cursor: "not-allowed" } : undefined
          }
        >
          {isSubmittingForm ? "Submitting..." : "View Recommended Results"}
        </button>
      </InpSafeForm>
      <div className="lg:hidden">
        <button
          type="button"
          className={`${styles.goBack} ${questionsProgressPct > 90 ? styles.visible : styles.hidden}`}
          onClick={handleGoBack}
        >
          <ArrowLeftIcon />
          <span>Go Back</span>
        </button>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className={styles.resultsPane}>
      <h3 className={styles.resultsHeading}>
        You want a strong start&mdash;we&rsquo;ll help you get there..
      </h3>

      <ul className={styles.highlights}>
        {RESULT_HIGHLIGHTS.map((h) => (
          <li key={h}>
            <CheckIcon className={styles.highlightCheck} />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      <div className={styles.resultsGrid}>
        <div className={styles.resultsColumn}>
          <h4 className={`${styles.resultsLabel} ${styles.resultsLabelDark}`}>
            Recommended Programs
          </h4>
          <div className={styles.cardStack}>
            {programs.slice(0, 2).map((p, idx) => {
              const selected = selectedProgramIndex === idx;
              return (
                <ProgramBrandCard
                  key={`prog-${idx}`}
                  program={p}
                  selected={selected}
                  onToggle={() => selectProgram(p, idx)}
                />
              );
            })}
          </div>
        </div>

        <div className={styles.plusSeparator} aria-hidden="true">
          +
        </div>

        <div className={styles.resultsColumn}>
          <h4 className={`${styles.resultsLabel} ${styles.resultsLabelGold}`}>
            Add-on Certificate
          </h4>
          <div className={styles.cardStack}>
            {certificate.slice(0, 1).map((c, idx) => {
              const selected = selectedCertIndex === idx;
              return (
                <CertificationCard
                  key={`cert-${idx}`}
                  program={c}
                  selected={selected}
                  onToggle={() => selectCertificate(c, idx)}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.resultsFooter}>
        <button
          type="button"
          className={styles.proceedBtn}
          onClick={handleProceed}
          disabled={isProceeding}
          style={
            isProceeding ? { opacity: 0.5, cursor: "not-allowed" } : undefined
          }
        >
          {isProceeding ? "Please wait..." : "Proceed"}
        </button>
        {proceedError && (
          <p
            style={{
              color: "#d92d20",
              fontSize: "0.875rem",
              marginTop: "0.5rem",
              textAlign: "center",
            }}
          >
            {proceedError}
          </p>
        )}
      </div>
    </div>
  );

  const modalClassName =
    step === "results"
      ? `${styles.modal} ${styles.modalResults}`
      : styles.modal;

  return (
    <section
      ref={sectionRef}
      id="program-finder"
      className={styles.section}
      aria-label="Find your program"
    >
      <div className={styles.banner}>
        <div className={styles.bg} aria-hidden="true" />

        <div className={styles.content}>
          <div className={styles.textBlock}>
            <p className={styles.heading}>
              Global Education Has a New Address:{" "}
              <span className={styles.highlight}>Yours.</span>
            </p>
            <p className={styles.subheading}>
              No matter your career path, we have programs tailored for
              tomorrow&rsquo;s global opportunities.
            </p>
          </div>

          <button type="button" className={styles.cta} onClick={openModal}>
            Find Your Program
          </button>
        </div>

        <div className={styles.streamsBar}>
          {STREAMS.map((s) => (
            <div className={styles.streamItem} key={s.label}>
              <UnivoImage
                src={s.icon}
                alt=""
                width={14}
                height={14}
                sizes="14px"
                loading="lazy"
                fetchPriority="low"
              />
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {isOpen && (
        <ModalPortal>
          <div
            className={styles.modalOverlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="program-selector-title"
          >
            <div className={modalClassName}>
              <button
                type="button"
                aria-label="Close"
                className={styles.closeBtn}
                onClick={closeModal}
              >
                &times;
              </button>

              {step !== "results" && renderLeftPane()}

              {step === "questions" && renderQuestions()}
              {step === "form" && renderForm()}
              {step === "results" && renderResults()}
            </div>
          </div>
        </ModalPortal>
      )}
    </section>
  );
};

export default ProgramSelectorBanner;
